import { notify, useModal } from '@billy/design-system'
import inRange from 'lodash/inRange'
import React, { createContext, ReactElement, ReactNode, useCallback, useContext, useMemo } from 'react'
import { useTranslation } from 'react-i18next'

import { queryClient } from '../../../../../config/queryClient'
import { NotificationKeys } from '../../../../../enums/notificationKeys'
import { QueryKeys } from '../../../../../enums/queryKeys'
import { FormComponent, useForm } from '../../../../../hooks'
import { APIError } from '../../../../../utils'
import { useVatDeclaration } from '../../../context/vatDeclarationContext'
import { useTrackVatDeclarationEvent } from '../../../hooks/useTrackVatDeclarationEvent'
import {
  UpdateVatDeclarationSettlementProps,
  useUpdateVatDeclarationSettlement,
} from '../../../hooks/useUpdateVatDeclarationSettlement'
import { defaultValues, getValidationSchema, VatDeclarationsDefinePeriodPayoutModalForm } from '../utils/formData'

interface ContextState {
  closeModal: () => void
  Form: FormComponent
  isProcessing: boolean
  submitForm: () => void
}

const VatDeclarationsDefinePayoutModalContext = createContext<ContextState | undefined>(undefined)

interface VatDeclarationsDefinePayoutModalContextProps {
  children?: ReactNode
  modalId: string
}

export const VatDeclarationsDefinePayoutModalContextProvider = ({
  children,
  modalId,
}: VatDeclarationsDefinePayoutModalContextProps): ReactElement => {
  const { t } = useTranslation()
  const { close: closeModal } = useModal(modalId)
  const { id, totalAmount } = useVatDeclaration()
  const trackEvent = useTrackVatDeclarationEvent()

  const { update, isProcessing } = useUpdateVatDeclarationSettlement({
    onError: (error: APIError | undefined) => {
      closeModal()
      notify({
        id: NotificationKeys.VatDeclarationsDefinePayout,
        message: error?.message || t('vat_declarations.define_payout_modal.fail'),
        variant: 'error',
      })
    },
    onSuccess: () => {
      queryClient.invalidateQueries(QueryKeys.VatDeclarations)
      closeModal()
      notify({
        id: NotificationKeys.VatDeclarationsDefinePayout,
        message: t('vat_declarations.define_payout_modal.success'),
        variant: 'success',
      })

      trackEvent('VAT Declaration Submitted (Client)', {
        context: 'manual',
      })
    },
  })

  const { Form, handleSubmit, setError } = useForm({
    defaultValues: useMemo(() => defaultValues, []),
    validationSchema: useMemo(() => getValidationSchema(), []),
  })

  const handleModalClose = useCallback(() => {
    closeModal()
  }, [closeModal])

  const handleSubmitForm = useCallback(
    (values: VatDeclarationsDefinePeriodPayoutModalForm) => {
      const isValidPaymentAmount = inRange(values.amount, 0, totalAmount) || values.amount === totalAmount

      if (!isValidPaymentAmount) {
        setError('amount', { message: t('vat_declarations.define_payout_modal.limit_error') })
        return
      }

      const definePeriodPaymentPayload: UpdateVatDeclarationSettlementProps = {
        customVatPayoutAmount: values.amount,
        isSettled: true,
        id,
      }

      update(definePeriodPaymentPayload)
    },
    [update, id, setError, totalAmount, t],
  )

  const submitForm = useCallback(() => {
    handleSubmit(handleSubmitForm)()
  }, [handleSubmit, handleSubmitForm])

  return (
    <VatDeclarationsDefinePayoutModalContext.Provider
      value={{
        closeModal: handleModalClose,
        Form,
        isProcessing,
        submitForm,
      }}
    >
      {children}
    </VatDeclarationsDefinePayoutModalContext.Provider>
  )
}

export const useVatDeclarationsDefinePeriodPayoutModal = () => {
  const context = useContext(VatDeclarationsDefinePayoutModalContext)

  if (!context) {
    throw new Error('VatDeclarationsDefinePeriodPayoutModalContext is missing in the module!')
  }

  return context
}
